import React, { useMemo, useState, useEffect } from 'react';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { ClientCombobox, CollaboratorCombobox } from '@/components/shared';
import { useClientsQuery } from '@/features/Clients/Data';
import { useRegrasClienteQuery } from '../../../../hooks/data';
import ConfiguracoesDrawer from '../../../SolicitacoesListPage/components/ConfiguracoesDrawer';
import type { FormFichaAtivo } from './ativoFicha.form';

interface AtivoPropriedadeCamposProps {
  form: FormFichaAtivo;
  onChange: (campo: keyof FormFichaAtivo, valor: string) => void;
}

/**
 * Propriedade num campo só: "Raro" ou um dos Clientes marcados em Configurações →
 * "Equipamento do Cliente" (se o ativo não é da Raro, o Cliente precisa estar lá — é de lá
 * que o Termo tira nome/CNPJ da empresa proprietária). Grava `propriedade` +
 * `clienteProprietarioId`. Cliente que não está na lista: botão abre essa configuração.
 * Projeto só aparece pra ativo da Raro — no de Cliente o Projeto é o próprio Cliente (ver camposDoForm).
 */
export default function AtivoPropriedadeCampos({ form, onChange }: AtivoPropriedadeCamposProps) {
  const { data: regras = [] } = useRegrasClienteQuery();
  const { data: clientes = [] } = useClientsQuery();
  const [configAberta, setConfigAberta] = useState(false);

  const opcoesCliente = useMemo(() => {
    const nomePorId = new Map(clientes.map((c) => [c.id, c.name]));
    const ids = new Set(regras.filter((r) => r.permiteEquipamentoCliente).map((r) => r.clienteId));
    // Cadastro antigo apontando pra Cliente que saiu da configuração — continua aparecendo.
    if (form.propriedade === 'cliente' && form.clienteProprietarioId) ids.add(form.clienteProprietarioId);
    return Array.from(ids)
      .map((id) => ({ id, nome: nomePorId.get(id) ?? 'Cliente não encontrado' }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [regras, clientes, form.propriedade, form.clienteProprietarioId]);

  // Cartão de acesso é sempre da Raro (decisão do SI) — fixa e trava a escolha.
  const sempreRaro = form.tipo === 'cartao_acesso';
  useEffect(() => {
    if (sempreRaro && form.propriedade !== 'raro') {
      onChange('propriedade', 'raro');
      onChange('clienteProprietarioId', '');
    }
  }, [sempreRaro, form.propriedade, onChange]);

  const valor = form.propriedade === 'cliente' ? form.clienteProprietarioId : form.propriedade;
  const alterarPropriedade = (novo: string) => {
    if (!novo || novo === 'raro') {
      onChange('propriedade', novo);
      onChange('clienteProprietarioId', '');
    } else {
      onChange('propriedade', 'cliente');
      onChange('clienteProprietarioId', novo);
    }
  };

  return (
    <section className="grid gap-3 sm:grid-cols-2">
      <div>
        <label className="block text-sm font-medium text-gray-700">Propriedade *</label>
        <select
          className="input-primary mt-1 w-full"
          value={valor}
          disabled={sempreRaro}
          title={sempreRaro ? 'Cartão de acesso é sempre da Raro' : undefined}
          onChange={(e) => alterarPropriedade(e.target.value)}
        >
          <option value="">Selecione...</option>
          <option value="raro">Raro</option>
          {opcoesCliente.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nome}
            </option>
          ))}
        </select>
        {!sempreRaro && (
        <button
          type="button"
          onClick={() => setConfigAberta(true)}
          className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:underline"
        >
          <Cog6ToothIcon className="h-3.5 w-3.5" />
          Cliente não está na lista? Configurar "Equipamento do Cliente"
        </button>
        )}
      </div>
      {form.propriedade === 'raro' && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Projeto (Cliente em uso)</label>
          <div className="mt-1">
            <ClientCombobox selectedId={form.projetoClienteId || null} onChange={(c) => onChange('projetoClienteId', c?.id ?? '')} placeholder="Buscar cliente..." />
          </div>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700">Colaborador (usuário)</label>
        <div className="mt-1">
          <CollaboratorCombobox selectedId={form.colaboradorAtualId || null} onChange={(c) => onChange('colaboradorAtualId', c?.id ?? '')} placeholder="Buscar colaborador..." />
        </div>
      </div>

      <ConfiguracoesDrawer isOpen={configAberta} onClose={() => setConfigAberta(false)} itemInicial="permiteEquipamentoCliente" />
    </section>
  );
}
